import React, { useState } from 'react';
import type { Team, TeamRole } from '../../../../types';
import { teamService } from '../../services/teamService';
import { useDialog } from '../../../../hooks/useDialog';

interface InviteCodeProps {
  team: Team;
  userRole: TeamRole;
  onInviteCodeUpdate: (newCode: string) => void;
}

export const InviteCode: React.FC<InviteCodeProps> = ({ team, userRole, onInviteCodeUpdate }) => {
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const { confirm } = useDialog();

  const canManage = userRole === 'OWNER' || userRole === 'ADMIN';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(team.inviteCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('复制失败:', err);
      // 可以显示错误提示
    }
  };

  const handleRegenerate = async () => {
    const confirmed = await confirm({
      title: '确认',
      message: '确定要重新生成邀请码吗？旧邀请码将失效。',
      confirmVariant: 'danger'
    });
    if (!confirmed) return;

    setRegenerating(true);
    try {
      const newCode = await teamService.regenerateInviteCode(team.id);
      onInviteCodeUpdate(newCode);
      // TODO: 显示成功提示
      console.log('邀请码已更新');
    } catch (error) {
      console.error('重新生成邀请码失败:', error);
      // TODO: 显示错误提示
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-700">团队邀请码</h3>
        <div className="text-xs text-gray-500">
          分享此邀请码让他人加入团队
        </div>
      </div>
      
      <div className="flex items-center space-x-2">
        <div className="flex-1 flex items-center bg-white rounded-lg border border-gray-200 px-3 py-2">
          <code className="text-lg font-mono text-gray-800 tracking-wider">
            {team.inviteCode}
          </code>
        </div>
        
        <button 
          onClick={handleCopy}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
            copied 
              ? 'bg-green-100 text-green-700 border border-green-200' 
              : 'bg-blue-500 text-white hover:bg-blue-600'
          }`}
        >
          {copied ? (
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              已复制
            </div>
          ) : (
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              复制
            </div>
          )}
        </button>
        
        {canManage && (
          <button 
            onClick={handleRegenerate}
            disabled={regenerating}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors duration-200"
          >
            {regenerating ? (
              <div className="flex items-center">
                <svg className="animate-spin w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                重新生成中
              </div>
            ) : (
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                重新生成
              </div>
            )}
          </button>
        )}
      </div>
      
      {!canManage && (
        <div className="mt-2 text-xs text-gray-500">
          💡 只有团队所有者和管理员可以重新生成邀请码
        </div>
      )}
    </div>
  );
};